import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  SelectChangeEvent,
  IconButton,
  Divider,
  useTheme,
  Theme,
  Grid2,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { DeliveryPartner, ShipmentsDetails } from "../../../models/Shipments";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import dayjs from "dayjs";
import CustomTypography from "../common/form/CustomTypography";
import AppSearchDropdownMulti from "../common/filter/AppSearchDropdownMulti";
import { ShipmentStatusEnum } from "../../../models/Shipments/ShipmentStatus";
import DatePicker2 from "../common/textFields/DatePicker2";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";

interface EditShipmentModalProps {
  open: boolean;
  onClose: () => void;
  shipment: ShipmentsDetails;
  deliveryPartners: DeliveryPartner[];
}

const Status = [
  { id: 1, value: "New" },
  { id: 2, value: "Ready To Ship" },
  { id: 3, value: "Shipped" },
  { id: 4, value: "Out For Delivery" },
  { id: 5, value: "Delivered" },
  { id: 6, value: "Return Requested" },
];

const textFieldSx = {
  "& .MuiInputLabel-root": { fontSize: "12px" },
  "& .MuiInputBase-input": { fontSize: "12px" },
};

const EditShipmentModal: React.FC<EditShipmentModalProps> = ({
  open,
  onClose,
  shipment,
  deliveryPartners,
}) => {
  const theme = useTheme<Theme>();
  const { shipmentStore, userStore } = useStores();
  const [shipmentDetails, setShipmentDetails] = useState<
    ShipmentsDetails | undefined
  >(undefined);
  const { showError, showSuccess } = useNotification();
  const [statusList, setStatusList] = useState(Status);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (shipment) {
      if (shipment.status === 8) {
        if (!statusList.some((status) => status.id === 8)) {
          setStatusList((prevStatus) => [
            ...prevStatus,
            { id: 8, value: "Cancelled" },
          ]);
        }
      } else {
        setStatusList((prevStatus) =>
          prevStatus.filter((status) => status.id !== 8),
        );
      }
      setShipmentDetails({
        id: shipment.id,
        first_name: shipment.first_name || "",
        last_name: shipment.last_name || "",
        email: shipment.email || "",
        order_notes: shipment.order_notes || "",
        address1: shipment.address1 || "",
        address2: shipment.address2 || "",
        country: shipment.country || "",
        state: shipment.state || "",
        city: shipment.city || "",
        pincode: shipment.pincode || "",
        phone: shipment.phone || "",
        alternate_phone: shipment.alternate_phone || "",
        expected_delivery_date: shipment.expected_delivery_date || undefined,
        dispatch_date: shipment.dispatch_date || undefined,
        delivery_partner_id: shipment.delivery_partner_id,
        delivery_partner_name: shipment.delivery_partner_name || "",
        status: shipment.status,
        awb: shipment.awb || "",
        awb_track_url: shipment.awb_track_url || "",
        additional_awb: shipment.additional_awb || "",
        shipping_cost:
          shipment.shipping_cost != null ? shipment.shipping_cost : 0,
        length: shipment.length != null ? String(shipment.length) : "",
        breadth: shipment.breadth != null ? String(shipment.breadth) : "",
        height: shipment.height != null ? String(shipment.height) : "",
        weight: shipment.weight != null ? String(shipment.weight) : "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment, onClose]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setShipmentDetails({
      ...shipmentDetails,
      [e.target.name]: e.target.value,
    });
  };

  const handleAdditionalAwbChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setShipmentDetails((prev) =>
      prev ? { ...prev, additional_awb: e.target.value } : prev,
    );
  };

  const getProcessedAdditionalAwb = (): string => {
    const raw = shipmentDetails?.additional_awb || "";
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .join(",");
  };

  const handleEdit = async () => {
    if (shipment.id) {
      const payload = {
        ...shipmentDetails!,
        additional_awb: getProcessedAdditionalAwb() || undefined,
      };
      const res = await shipmentStore.editShipment(
        shipment.id,
        payload,
        userStore,
      );
      if (res?.success) {
        showSuccess("Shipment updated successfully");
      } else {
        showError("Error", shipmentStore.error || "Error saving shipment");
      }
      onClose();
    } else {
      onClose();
    }
  };

  const canEditShipments = userStore.hasPermission(
    EmployeeRoles.EDIT_SHIPMENTS,
    true,
  );

  const handleCopyTrackingLink = async () => {
    const url = shipmentDetails?.awb_track_url?.trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      showError("Error", "Failed to copy tracking link");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            background: `linear-gradient(135deg, ${theme.palette.customColors?.white || "#fff"} 0%, ${theme.palette.customColors?.lightGreyColor || "#f5f5f5"} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors?.blueGradientStart || "#1976d2"} 0%, ${theme.palette.customColors?.blueGradientEnd || "#42a5f5"} 50%, ${theme.palette.customColors?.blueGradientStart || "#1976d2"} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
          }}
        >
          <CustomTypography variant="heading4">
            Edit Shipment #{shipment.id}
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors?.greyTextColor || "#666",
              "&:hover": {
                backgroundColor: theme.palette.customColors?.lightGreyColor,
                color: theme.palette.customColors?.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors?.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors?.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors?.greyTextColor,
              },
            },
          }}
        >
          {/* Recipient Information */}
          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              sx={{ fontWeight: 600 }}
              style={{ marginBottom: "16px" }}
            >
              Recipient Information
            </CustomTypography>
            <Grid2 container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  name="first_name"
                  value={shipmentDetails?.first_name}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  name="last_name"
                  value={shipmentDetails?.last_name}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  name="email"
                  type="email"
                  value={shipmentDetails?.email}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  name="phone"
                  value={shipmentDetails?.phone}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alternate Number"
                  name="alternate_phone"
                  value={shipmentDetails?.alternate_phone}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
            </Grid2>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Shipping Address */}
          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              sx={{ fontWeight: 600 }}
              style={{ marginBottom: "16px" }}
            >
              Shipping Address
            </CustomTypography>
            <Grid2 container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Country"
                  name="country"
                  value={shipmentDetails?.country}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  name="state"
                  value={shipmentDetails?.state}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  name="city"
                  value={shipmentDetails?.city}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pincode"
                  name="pincode"
                  value={shipmentDetails?.pincode}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Shipping Address 1"
                  name="address1"
                  value={shipmentDetails?.address1}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Shipping Address 2"
                  name="address2"
                  value={shipmentDetails?.address2}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
            </Grid2>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Package Details */}
          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              sx={{ fontWeight: 600 }}
              style={{ marginBottom: "16px" }}
            >
              Package Details
            </CustomTypography>
            <Grid2 container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Length"
                  name="length"
                  type="number"
                  value={shipmentDetails?.length}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Breadth"
                  name="breadth"
                  type="number"
                  value={shipmentDetails?.breadth}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Height"
                  name="height"
                  type="number"
                  value={shipmentDetails?.height}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Weight"
                  name="weight"
                  type="number"
                  value={shipmentDetails?.weight}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
            </Grid2>
          </Box>

          {/* Order Notes */}
          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              sx={{ fontWeight: 600 }}
              style={{ marginBottom: "16px" }}
            >
              Order Notes
            </CustomTypography>
            <Grid2 container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order Notes"
                  name="order_notes"
                  multiline
                  minRows={2}
                  value={shipmentDetails?.order_notes}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
            </Grid2>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Delivery & Tracking */}
          <Box sx={{ mb: 2 }}>
            <CustomTypography
              variant="heading6"
              sx={{ fontWeight: 600 }}
              style={{ marginBottom: "4px" }}
            >
              Delivery & Tracking
            </CustomTypography>
            <CustomTypography variant="body3" style={{ marginBottom: "16px" }}>
              Note:- Shipment status can be moved forward only after dimensions,
              delivery partner and packs have been added.
            </CustomTypography>
            <Grid2 container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DatePicker2
                  label="Expected Delivery Date"
                  type="date"
                  minDate={dayjs()}
                  onChange={(newValue) => {
                    handleChange({
                      target: {
                        name: "expected_delivery_date",
                        value: newValue ? newValue : "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  value={
                    shipmentDetails?.expected_delivery_date
                      ? dayjs(shipmentDetails.expected_delivery_date)
                      : null
                  }
                  disabled={!canEditShipments}
                  zIndex={3000}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <AppSearchDropdownMulti
                  borderColor={theme.palette.customColors.blackBorder}
                  isMultiSelect={false}
                  title="Select Delivery Partner"
                  fontSize="0.8rem"
                  value={
                    shipmentDetails?.delivery_partner_id
                      ? [
                          {
                            id: String(shipmentDetails.delivery_partner_id),
                            value: String(
                              shipmentDetails.delivery_partner_name,
                            ),
                          },
                        ]
                      : undefined
                  }
                  list={deliveryPartners?.map((partner) => ({
                    id: String(partner.delivery_partner_id),
                    value: String(partner.delivery_partner_name),
                  }))}
                  local={true}
                  handleSelect={(selectedItems) => {
                    if (selectedItems.length === 0) {
                      setShipmentDetails((prev) =>
                        prev
                          ? {
                              ...prev,
                              delivery_partner_id: -1,
                              delivery_partner_name: "",
                            }
                          : prev,
                      );
                    } else {
                      setShipmentDetails((prev) =>
                        prev
                          ? {
                              ...prev,
                              delivery_partner_id: Number(selectedItems[0].id),
                              delivery_partner_name: selectedItems[0].value,
                            }
                          : prev,
                      );
                    }
                  }}
                  disabled={!canEditShipments}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <AppSearchDropdownMulti
                  borderColor={theme.palette.customColors.blackBorder}
                  isMultiSelect={false}
                  title="Select Status"
                  fontSize="0.8rem"
                  value={
                    shipmentDetails?.status
                      ? [
                          {
                            id: String(shipmentDetails.status),
                            value:
                              statusList.find(
                                (status) =>
                                  status.id === shipmentDetails.status,
                              )?.value || "Select Status",
                          },
                        ]
                      : undefined
                  }
                  list={
                    !shipmentDetails?.length ||
                    !shipmentDetails?.breadth ||
                    !shipmentDetails?.height ||
                    !shipmentDetails?.weight ||
                    !shipmentDetails?.delivery_partner_id
                      ? statusList
                          ?.filter(
                            (status) => status.id === ShipmentStatusEnum.NEW,
                          )
                          ?.map((status) => ({
                            id: String(status.id),
                            value: String(status.value),
                          })) || []
                      : statusList?.map((status) => ({
                          id: String(status.id),
                          value: String(status.value),
                        })) || []
                  }
                  local={true}
                  handleSelect={(selectedItems) => {
                    if (selectedItems.length === 0) {
                      setShipmentDetails((prev) =>
                        prev
                          ? {
                              ...prev,
                              status: -1,
                              status_label: "",
                            }
                          : prev,
                      );
                    } else {
                      setShipmentDetails((prev) =>
                        prev
                          ? {
                              ...prev,
                              status: Number(selectedItems[0].id),
                              status_label: selectedItems[0].value,
                            }
                          : prev,
                      );
                    }
                  }}
                  disabled={!canEditShipments}
                />

                {/* <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={shipmentDetails?.status || ""}
                    onChange={handleDropdownChange}
                    label="Status"
                    disabled={!canEditShipments}
                    MenuProps={{
                      sx: { zIndex: 2100 },
                      PaperProps: {
                        sx: { zIndex: 2100 },
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                    }}
                    sx={textFieldSx}
                  >
                    <MenuItem key={"status-default-value"} value={-1}>
                      None
                    </MenuItem>
                    {statusList.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DatePicker2
                  type="date"
                  minDate={dayjs().subtract(1, "week")}
                  label="Dispatch Date"
                  onChange={(newValue) => {
                    handleChange({
                      target: {
                        name: "dispatch_date",
                        value: newValue ? newValue : "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  value={
                    shipmentDetails?.dispatch_date
                      ? dayjs(shipmentDetails.dispatch_date)
                      : null
                  }
                  disabled={!canEditShipments}
                  zIndex={3000}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Shipping Cost"
                  name="shipping_cost"
                  type="number"
                  value={shipmentDetails?.shipping_cost || ""}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="AWB Number"
                  name="awb"
                  value={shipmentDetails?.awb || ""}
                  onChange={handleChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tracking Link"
                    name="awb_track_url"
                    value={shipmentDetails?.awb_track_url || ""}
                    onChange={handleChange}
                    disabled={!canEditShipments}
                    sx={textFieldSx}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Copy tracking link"
                              onClick={handleCopyTrackingLink}
                              disabled={
                                !canEditShipments ||
                                !shipmentDetails?.awb_track_url?.trim()
                              }
                              edge="end"
                              sx={{
                                p: { xs: 0.75, sm: 1 },
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <ContentCopyIcon
                                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  {linkCopied && (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "success.main",
                        mt: 0.5,
                      }}
                    >
                      Tracking link copied!
                    </Typography>
                  )}
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Additional AWB Numbers"
                  name="additional_awb"
                  placeholder="Comma separated AWB numbers for tracking"
                  value={shipmentDetails?.additional_awb || ""}
                  onChange={handleAdditionalAwbChange}
                  disabled={!canEditShipments}
                  sx={textFieldSx}
                  helperText="Enter comma-separated AWB numbers. Values will be trimmed before saving."
                />
              </Grid2>
            </Grid2>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
            backgroundColor: theme.palette.customColors?.lightGreyColor,
            gap: 2,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: theme.palette.customColors?.greyDarkborder,
              color: theme.palette.customColors?.greyTextColor,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                borderColor: theme.palette.customColors?.greyTextColor,
                backgroundColor: theme.palette.customColors?.lightGreyColor,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={!canEditShipments}
            sx={{
              backgroundColor:
                theme.palette.customColors?.EmeraldGreen ||
                theme.palette.primary.main,
              color: theme.palette.customColors?.white || "#fff",
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greenDark ||
                  theme.palette.primary.dark,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 12px ${theme.palette.customColors?.EmeraldGreen || theme.palette.primary.main}30`,
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditShipmentModal;
